import { z } from "zod";

/**
 * The list query vocabulary. These exact names are what `useServerTable` emits on
 * the client and what list endpoints accept — one spelling, so URL parameters, API
 * parameters and cache keys cannot drift apart.
 */
export const QUERY_PARAMS = {
  page: "page",
  perPage: "perPage",
  sort: "sort",
  search: "q",
} as const;

export const PER_PAGE_OPTIONS = [10, 20, 30, 50, 100] as const;
export const DEFAULT_PER_PAGE = 30;

/** `field:asc` / `field:desc`. Validated against a per-endpoint allowlist server-side. */
export const sortSchema = z
  .string()
  .regex(/^[a-zA-Z][a-zA-Z0-9_]*:(asc|desc)$/, "Expected `field:asc` or `field:desc`");

export const listQuerySchema = z.object({
  /** 1-indexed, and omitted from the URL on page 1. */
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce
    .number()
    .int()
    .refine((n): n is (typeof PER_PAGE_OPTIONS)[number] => (PER_PAGE_OPTIONS as readonly number[]).includes(n), {
      message: `perPage must be one of ${PER_PAGE_OPTIONS.join(", ")}`,
    })
    .default(DEFAULT_PER_PAGE),
  sort: sortSchema.optional(),
  q: z.string().trim().min(1).optional(),
});

export type ListQuery = z.infer<typeof listQuerySchema>;

/**
 * The list envelope. `total` is the count **after** filters and before pagination —
 * it is what the pagination control needs to say "1–30 of 412" honestly.
 */
export type ListResponse<T> = {
  items: T[];
  page: number;
  perPage: number;
  total: number;
};

export function listResponseSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    page: z.number().int(),
    perPage: z.number().int(),
    total: z.number().int(),
  });
}

/**
 * Multi-value filters travel comma-joined (`owner=id1,id2`) — the API contract's own
 * vocabulary, and exactly what the client's `useFilterParamList` convention writes.
 *
 * A `preprocess` rather than a `transform`, because the split has to happen *before* the
 * item schema runs: on the wire `owner=a,b` is one string, and what it means is two uuids.
 *
 * An absent, empty or blank-only parameter becomes **`undefined`, not `[]`**. That is
 * load-bearing: an empty array would have to mean either "everything" or "nothing", and a
 * where-builder cannot tell which — a stray `?stage=` returning zero rows would be
 * indistinguishable from a genuinely empty result. Absent means unfiltered, once.
 */
export function csvListSchema<T extends z.ZodTypeAny>(item: T) {
  return z.preprocess((value) => {
    if (typeof value !== "string") return value;
    const parts = value
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);
    return parts.length > 0 ? parts : undefined;
  }, z.array(item).optional());
}
